import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  MoneyInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mlm/ui';
import { Decimal, orderCustomFeeLines, type OrderDocumentPayload } from '@mlm/shared';
import {
  api,
  apiErrorMessage,
  courierLogoUrl,
  getOrderDocuments,
  getTenantFeatures,
  issueOrderDocument,
  type OrderDocumentsResponse,
} from '../lib/api';
import { OrderStockUsageSection } from '../components/inventory/order-stock-usage';

type CourierLabel = {
  id: string;
  label: string;
  logoKey: string | null;
  trackingUrlTemplate: string | null;
};

async function fetchCourierLabels(): Promise<CourierLabel[]> {
  return (await api.get<{ couriers: CourierLabel[] }>('/couriers/labels')).data.couriers;
}

/** Courier logo + name as a clear chip (logo falls back to text on load error). */
function CourierChip({ courier }: { courier: CourierLabel | null }) {
  if (!courier) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-2 py-0.5 text-xs font-medium">
      {courier.logoKey && (
        <img
          src={courierLogoUrl(courier.id)}
          alt=""
          className="h-4 w-auto"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
      {courier.label}
    </span>
  );
}

type AdminOrderRow = {
  id: string;
  code: string;
  type: 'ecom' | 'pos' | 'slot_activation';
  status: string;
  total: string;
  totalPv: string;
  buyerUserId: string;
  buyerSlotId: string | null;
  branchId: string | null;
  createdAt: string;
  buyerEmail: string;
  buyerFullName: string | null;
  slotCode: string | null;
  branchCode: string | null;
  trackingNumber: string | null;
  paymentCurrency: string;
  /** Registry code (or, for older orders, the manual payment's method). */
  paymentMethodCode: string | null;
  paymentMethodLabel: string | null;
};

type TransitionEdge = {
  from: string;
  to: string;
  actor: 'admin' | 'member' | 'system';
  channels: ('shipping' | 'pickup')[];
  requires?: 'proof_uploaded' | 'tracking_number';
};

type OrderActions = {
  /** The order is a manual-fee one still waiting for an admin to price it. */
  awaitingShippingFee?: boolean;
  admin: TransitionEdge[];
  member: TransitionEdge[];
};

type ManualPayment = {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  method: string;
  amount: string;
  currency: string;
  // QoL Slice 3 — member-typed reference is blinded; only `referenceProvided`
  // is shipped to the SPA. The admin types the value from the picture into
  // the review modal, and the verified value lives in `adminTypedReference`.
  referenceProvided: boolean;
  adminTypedReference: string | null;
  proofR2Key: string | null;
  scanStatus: 'pending' | 'clean' | 'infected' | null;
  rejectionReason: string | null;
  createdAt: string;
  proofUrl: string | null;
};

type RefundEligibility = {
  orderId: string;
  items: Array<{
    orderItemId: string;
    productId: string;
    productName: string;
    qty: number;
    isKit: boolean;
    redeemedCount: number;
    transferredUnredeemedCount: number;
    refundable: boolean;
    reason?: 'kit_code_redeemed';
  }>;
  allRefundable: boolean;
  anyRefundable: boolean;
  transferredUnredeemedTotal: number;
};

type OrderDetail = {
  id: string;
  code: string;
  type: 'ecom' | 'pos' | 'slot_activation';
  status: string;
  subtotal: string;
  discountTotal: string;
  shippingFee: string;
  /** `customFeeAmount` is the TOTAL of every fee; `customFees` the breakdown.
   *  Orders placed before the breakdown existed carry only the first two. */
  customFeeLabel: string | null;
  customFeeAmount: string;
  customFees?: Array<{ code: string | null; label: string; amount: string }> | null;
  total: string;
  totalPv: string;
  metadata: Record<string, unknown> | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  courierId: string | null;
  shipToAddress: Record<string, unknown> | null;
  buyerUserId: string;
  buyerSlotId: string | null;
  branchId: string | null;
  createdAt: string;
  items: Array<{
    id: string;
    productId: string;
    qty: number;
    unitPrice: string;
    unitPriceBase: string;
    appliedDiscountPct: string;
    unitPv: string;
    productName?: string;
    productSku?: string;
    productImages?: string[];
    variantId?: string | null;
    variantName?: string | null;
  }>;
  statusHistory: Array<{ status: string; note: string | null; changedAt: string }>;
  buyer?: { id: string; fullName: string | null; email: string; phone: string | null } | null;
  shipFromBranch?: { id: string; code: string; name: string } | null;
  paymentMethodCode?: string | null;
  paymentMethodLabel?: string | null;
};

type ShipToAddress = {
  fullName?: string;
  phone?: string;
  line1?: string;
  barangay?: string;
  city?: string;
  province?: string;
  region?: string;
  regionCode?: string;
  postalCode?: string;
} & Record<string, unknown>;

function formatAddressLines(a: ShipToAddress | null | undefined): string[] {
  if (!a) return [];
  const lines: string[] = [];
  if (a.line1) lines.push(String(a.line1));
  const second = [a.barangay, a.city]
    .map((p) => p && String(p).trim())
    .filter(Boolean)
    .join(', ');
  if (second) lines.push(second);
  const third = [a.province, a.region]
    .map((p) => p && String(p).trim())
    .filter(Boolean)
    .join(', ');
  if (third) lines.push(third);
  if (a.postalCode) lines.push(String(a.postalCode));
  return lines;
}

const SHIPPING_LADDER = [
  'pending',
  'to_pay',
  'paid',
  'to_ship',
  'shipping',
  'delivered',
  'completed',
] as const;
const PICKUP_LADDER = ['pending', 'to_pay', 'paid', 'to_pick', 'completed'] as const;

function isShippingChannel(order: { type: string; shipToAddress?: unknown }): boolean {
  return order.type === 'ecom' && !!order.shipToAddress;
}

function statusBadgeVariant(status: string): 'success' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'completed' || status === 'paid' || status === 'delivered') return 'success';
  if (status === 'cancelled' || status === 'refunded') return 'destructive';
  return 'secondary';
}

const PRIMARY_LABELS: Record<string, string> = {
  to_pay: 'Mark to-pay',
  paid: 'Approve payment',
  to_ship: 'Mark to-ship',
  shipping: 'Ship…',
  delivered: 'Mark delivered',
  to_pick: 'Ready for pickup',
  completed: 'Mark received',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  to_pay: 'To pay',
  paid: 'Paid',
  to_ship: 'To ship',
  shipping: 'Shipping',
  delivered: 'Delivered',
  to_pick: 'Ready for pickup',
  to_release: 'To release',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

// A POS ('pos') order is a walk-in cashier sale; surface it as "Sales Order" in
// admin. Label-only — the underlying `orders.type` enum value stays `'pos'`.
const ORDER_TYPE_LABELS: Record<string, string> = {
  ecom: 'Ecom',
  pos: 'Sales Order',
  slot_activation: 'Slot activation',
};

// Dashboard "orders awaiting action" deep-link value: a comma list the API's
// status filter accepts, surfaced as one pseudo-option in the status Select.
const AWAITING_ACTION_STATUSES = 'pending,to_pay,paid,to_pick,to_ship,to_release';

type OrderSource = 'inhouse' | 'merchant' | 'all';
function sourceFromParam(v: string | null): OrderSource {
  return v === 'merchant' || v === 'all' ? v : 'inhouse';
}

export function AdminOrdersPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status') ?? '';
  const urlSource = searchParams.get('source');
  // Carried in by the dashboard's "orders awaiting action" tile when its branch
  // filter is on, so the count clicked and the list landed on agree. The server
  // clamps it for branch-scoped operators, so it can't widen anyone's view.
  const urlBranchId = searchParams.get('branchId') ?? '';
  const [page, setPage] = useState(1);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [type, setType] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>(urlStatus);
  const [source, setSource] = useState<OrderSource>(sourceFromParam(urlSource));

  // The URL wins on navigation (dashboard deep link in, sidebar "Orders" out —
  // React Router re-renders in place, so mount-time seeding alone would leave
  // a stale filter behind). In-page Select changes stay state-only.
  useEffect(() => {
    setStatusFilter(urlStatus);
    setSource(sourceFromParam(urlSource));
    setPage(1);
  }, [urlStatus, urlSource, urlBranchId]);
  const [courierId, setCourierId] = useState<string>('');
  const [buyerSearch, setBuyerSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [drawerOrderId, setDrawerOrderId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const q = useQuery({
    queryKey: [
      'admin-orders',
      type,
      statusFilter,
      source,
      courierId,
      buyerSearch,
      dateFrom,
      dateTo,
      urlBranchId,
      page,
    ],
    queryFn: async () =>
      (
        await api.get<{ rows: AdminOrderRow[]; total: number; page: number; pageSize: number }>(
          '/admin/orders',
          {
            params: {
              type: type || undefined,
              status: statusFilter || undefined,
              source,
              courierId: courierId || undefined,
              branchId: urlBranchId || undefined,
              buyerSearch: buyerSearch || undefined,
              dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
              dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
              page,
              pageSize: 50,
            },
          },
        )
      ).data,
  });

  const couriersQ = useQuery({ queryKey: ['courier-labels'], queryFn: fetchCourierLabels });
  const couriers = couriersQ.data ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Orders</h1>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Source</label>
            <Select
              value={source}
              onValueChange={(v) => {
                setSource(v as 'inhouse' | 'merchant' | 'all');
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inhouse">Inhouse only</SelectItem>
                <SelectItem value="merchant">Merchant only</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Type</label>
            <Select
              value={type || '__all__'}
              onValueChange={(v) => {
                setType(v === '__all__' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="ecom">{ORDER_TYPE_LABELS.ecom}</SelectItem>
                <SelectItem value="pos">{ORDER_TYPE_LABELS.pos}</SelectItem>
                <SelectItem value="slot_activation">{ORDER_TYPE_LABELS.slot_activation}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Courier</label>
            <Select
              value={courierId || '__all__'}
              onValueChange={(v) => {
                setCourierId(v === '__all__' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All couriers</SelectItem>
                {couriers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Status</label>
            <Select
              value={statusFilter || '__all__'}
              onValueChange={(v) => {
                setStatusFilter(v === '__all__' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value={AWAITING_ACTION_STATUSES}>Awaiting action</SelectItem>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Search</label>
            <Input
              placeholder="Member / Slot code / Tracking #"
              value={buyerSearch}
              onChange={(e) => {
                setBuyerSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">From</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">To</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {q.data && (
              <span>
                Orders{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  ({q.data.total} total · page {page})
                </span>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {errMsg && (
            <p className="m-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errMsg}
            </p>
          )}
          {q.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !q.data?.rows.length ? (
            <p className="p-6 text-sm text-muted-foreground">No orders match.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Buyer</th>
                  <th className="px-4 py-2">Slot</th>
                  <th className="px-4 py-2">Branch</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Payment</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 w-64">Actions</th>
                  <th className="px-4 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((o) => (
                  <OrderRow
                    key={o.id}
                    order={o}
                    onOpenDrawer={() => setDrawerOrderId(o.id)}
                    onActionRequested={(p) => setPendingAction(p)}
                  />
                ))}
              </tbody>
            </table>
          )}
          {q.data && q.data.total > q.data.pageSize && (
            <div className="flex justify-end gap-2 p-3">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="text-sm px-2 py-1 rounded border disabled:opacity-50"
              >
                Prev
              </button>
              <button
                disabled={page * q.data.pageSize >= q.data.total}
                onClick={() => setPage((p) => p + 1)}
                className="text-sm px-2 py-1 rounded border disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      <OrderDrawer
        orderId={drawerOrderId}
        onClose={() => setDrawerOrderId(null)}
        onActionRequested={(p) => setPendingAction(p)}
      />

      {pendingAction?.kind === 'transition' && pendingAction.to === 'paid' ? (
        <ApprovePaymentDialog
          orderId={pendingAction.orderId}
          orderCode={pendingAction.orderCode}
          onClose={() => setPendingAction(null)}
          onSuccess={() => {
            setPendingAction(null);
            setErrMsg(null);
            queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
            queryClient.invalidateQueries({ queryKey: ['admin-order-actions'] });
            queryClient.invalidateQueries({ queryKey: ['admin-order-detail'] });
            queryClient.invalidateQueries({ queryKey: ['admin-order-eligibility'] });
            queryClient.invalidateQueries({ queryKey: ['admin-order-manual-payment'] });
            // Billing-tab document eligibility follows the status too.
            queryClient.invalidateQueries({ queryKey: ['admin-order-documents'] });
          }}
          onError={(msg) => setErrMsg(msg)}
        />
      ) : pendingAction?.kind === 'shippingFee' ? (
        <SetShippingFeeDialog
          pending={pendingAction}
          onClose={() => setPendingAction(null)}
          onSuccess={() => {
            setPendingAction(null);
            setErrMsg(null);
            queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
            queryClient.invalidateQueries({ queryKey: ['admin-order-actions'] });
            queryClient.invalidateQueries({ queryKey: ['admin-order-detail'] });
          }}
          onError={(msg) => setErrMsg(msg)}
        />
      ) : (
        <ActionConfirmDialog
          pending={pendingAction}
          onClose={() => setPendingAction(null)}
          onSuccess={() => {
            setPendingAction(null);
            setErrMsg(null);
            queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
            // Invalidate ALL per-order action caches — row + drawer share the
            // same key prefix and a stale row would otherwise re-fire the just-
            // completed transition on next click ("illegal_transition").
            queryClient.invalidateQueries({ queryKey: ['admin-order-actions'] });
            queryClient.invalidateQueries({ queryKey: ['admin-order-detail'] });
            queryClient.invalidateQueries({ queryKey: ['admin-order-eligibility'] });
            queryClient.invalidateQueries({ queryKey: ['admin-order-manual-payment'] });
            // Billing-tab document eligibility follows the status too (a
            // refund flips it from "issue billing" to "issue credit memo").
            queryClient.invalidateQueries({ queryKey: ['admin-order-documents'] });
          }}
          onError={(msg) => setErrMsg(msg)}
        />
      )}
    </div>
  );
}

type PendingAction =
  | {
      kind: 'transition';
      orderId: string;
      orderCode: string;
      from: string;
      to: string;
      needsTracking?: boolean;
    }
  | { kind: 'cancel'; orderId: string; orderCode: string }
  | { kind: 'refund'; orderId: string; orderCode: string }
  | {
      kind: 'shippingFee';
      orderId: string;
      orderCode: string;
      subtotal: string;
      currentFee: string;
    };

/** One right-aligned money line in the drawer's Payment breakdown. */
function MoneyRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={muted ? 'text-muted-foreground italic' : 'tabular-nums'}>{value}</dd>
    </div>
  );
}

function OrderRow({
  order,
  onOpenDrawer,
  onActionRequested,
}: {
  order: AdminOrderRow;
  onOpenDrawer: () => void;
  onActionRequested: (p: PendingAction) => void;
}) {
  const actionsQ = useQuery({
    queryKey: ['admin-order-actions', order.id],
    queryFn: async () => (await api.get<OrderActions>(`/admin/orders/${order.id}/actions`)).data,
    staleTime: 5_000,
  });

  const adminEdges = actionsQ.data?.admin ?? [];
  const primary = pickPrimary(adminEdges);
  const cancelEdge = adminEdges.find((e) => e.to === 'cancelled');
  const refundEdge = adminEdges.find((e) => e.to === 'refunded');

  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="px-4 py-2 text-xs text-muted-foreground cursor-pointer" onClick={onOpenDrawer}>
        {new Date(order.createdAt).toLocaleString()}
      </td>
      <td className="px-4 py-2 font-mono text-xs cursor-pointer" onClick={onOpenDrawer}>
        {order.code}
      </td>
      <td className="px-4 py-2">
        <Link to={`/members/${order.buyerUserId}`} className="hover:underline">
          {order.buyerFullName ?? order.buyerEmail}
        </Link>
      </td>
      <td className="px-4 py-2 font-mono text-xs">
        {order.buyerSlotId ? (
          <Link to={`/slots/${order.buyerSlotId}`} className="hover:underline">
            {order.slotCode ?? order.buyerSlotId.slice(0, 8)}
          </Link>
        ) : (
          ''
        )}
      </td>
      <td className="px-4 py-2 text-xs">{order.branchCode ?? ''}</td>
      <td className="px-4 py-2">
        <Badge variant="outline">{ORDER_TYPE_LABELS[order.type] ?? order.type}</Badge>
      </td>
      <td className="px-4 py-2 text-xs">
        {order.type === 'ecom' ? (order.paymentMethodLabel ?? '—') : '—'}
      </td>
      <td className="px-4 py-2 cursor-pointer" onClick={onOpenDrawer}>
        <Badge variant={statusBadgeVariant(order.status)}>
          {STATUS_LABELS[order.status] ?? order.status}
        </Badge>
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-wrap items-center gap-1">
          <Button size="sm" variant="outline" onClick={onOpenDrawer}>
            View Order
          </Button>
          {primary ? (
            <Button
              size="sm"
              variant={
                primary.to === 'cancelled' || primary.to === 'refunded' ? 'destructive' : 'default'
              }
              onClick={() =>
                onActionRequested({
                  kind: 'transition',
                  orderId: order.id,
                  orderCode: order.code,
                  from: primary.from,
                  to: primary.to,
                  needsTracking: primary.requires === 'tracking_number',
                })
              }
            >
              {PRIMARY_LABELS[primary.to] ?? primary.to}
            </Button>
          ) : null}
          {(cancelEdge || refundEdge) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost">
                  ⋯
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {cancelEdge && (
                  <DropdownMenuItem
                    onSelect={() =>
                      onActionRequested({
                        kind: 'cancel',
                        orderId: order.id,
                        orderCode: order.code,
                      })
                    }
                  >
                    Cancel order
                  </DropdownMenuItem>
                )}
                {refundEdge && (
                  <DropdownMenuItem
                    className="text-destructive"
                    onSelect={() =>
                      onActionRequested({
                        kind: 'refund',
                        orderId: order.id,
                        orderCode: order.code,
                      })
                    }
                  >
                    Refund order
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </td>
      <td className="px-4 py-2 text-right tabular-nums font-mono">{order.total}</td>
    </tr>
  );
}

/**
 * Pick the single "primary" forward-action button for the Actions cell.
 * Skips cancel/refund (those live in the kebab) and picks the most-progressed
 * forward edge — usually there's only one, but ranks deterministically when
 * multiple admin edges exist (e.g. delivered → completed admin override).
 */
function pickPrimary(edges: TransitionEdge[]): TransitionEdge | undefined {
  const FORWARD = ['to_pay', 'paid', 'to_ship', 'shipping', 'delivered', 'to_pick', 'completed'];
  return edges
    .filter((e) => FORWARD.includes(e.to))
    .sort((a, b) => FORWARD.indexOf(a.to) - FORWARD.indexOf(b.to))[0];
}

function OrderDrawer({
  orderId,
  onClose,
  onActionRequested,
}: {
  orderId: string | null;
  onClose: () => void;
  onActionRequested: (p: PendingAction) => void;
}) {
  const detailQ = useQuery({
    queryKey: ['admin-order-detail', orderId],
    queryFn: async () => (await api.get<OrderDetail>(`/orders/${orderId}`)).data,
    enabled: !!orderId,
  });
  const actionsQ = useQuery({
    queryKey: ['admin-order-actions', orderId],
    queryFn: async () => (await api.get<OrderActions>(`/admin/orders/${orderId}/actions`)).data,
    enabled: !!orderId,
  });
  const eligibilityQ = useQuery({
    queryKey: ['admin-order-eligibility', orderId],
    queryFn: async () =>
      (await api.get<RefundEligibility>(`/admin/orders/${orderId}/refund-eligibility`)).data,
    enabled: !!orderId,
  });
  const couriersQ = useQuery({ queryKey: ['courier-labels'], queryFn: fetchCourierLabels });

  // Accounting Phase 2: the Billing tab (order documents) only exists for
  // accounting tenants — same shared cache key as every other feature gate.
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const accountingEnabled = featuresQ.data?.accounting_enabled === true;
  const documentsQ = useQuery({
    queryKey: ['admin-order-documents', orderId],
    queryFn: () => getOrderDocuments(orderId!),
    enabled: !!orderId && accountingEnabled,
  });

  const order = detailQ.data;
  const courier = couriersQ.data?.find((c) => c.id === order?.courierId) ?? null;
  const ladder = useMemo(() => {
    if (!order) return [] as readonly string[];
    return isShippingChannel(order) ? SHIPPING_LADDER : PICKUP_LADDER;
  }, [order]);
  const historyByStatus = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of order?.statusHistory ?? []) m.set(h.status, h.changedAt);
    return m;
  }, [order]);

  return (
    <Dialog
      open={!!orderId}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {order ? (
              <span className="flex items-center gap-3">
                <span className="font-mono text-sm">{order.code}</span>
                <Badge variant="outline">{ORDER_TYPE_LABELS[order.type] ?? order.type}</Badge>
                <Badge variant={statusBadgeVariant(order.status)}>
                  {STATUS_LABELS[order.status] ?? order.status}
                </Badge>
              </span>
            ) : (
              'Order detail'
            )}
          </DialogTitle>
        </DialogHeader>

        {!order ? (
          <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
        ) : (
          <Tabs defaultValue="progress" className="mt-6">
            <TabsList className="no-print">
              <TabsTrigger value="progress">Progress</TabsTrigger>
              <TabsTrigger value="packing">Packing slip</TabsTrigger>
              {accountingEnabled && <TabsTrigger value="billing">Billing</TabsTrigger>}
            </TabsList>

            <TabsContent value="progress" className="space-y-6">
              <section>
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  Progress
                </h3>
                <ol className="space-y-1.5">
                  {ladder.map((step) => {
                    const reachedIdx = ladder.indexOf(order.status as (typeof ladder)[number]);
                    const stepIdx = ladder.indexOf(step);
                    const reached = stepIdx <= reachedIdx;
                    const current = step === order.status;
                    const ts = historyByStatus.get(step);
                    return (
                      <li key={step} className="flex items-center gap-2 text-sm">
                        <span
                          className={
                            'inline-block h-2 w-2 rounded-full ' +
                            (current ? 'bg-primary' : reached ? 'bg-primary/40' : 'bg-muted')
                          }
                        />
                        <span
                          className={
                            current ? 'font-medium' : reached ? '' : 'text-muted-foreground'
                          }
                        >
                          {STATUS_LABELS[step] ?? step}
                        </span>
                        {ts && (
                          <span className="text-xs text-muted-foreground ml-auto">
                            {new Date(ts).toLocaleString()}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
                {isShippingChannel(order) && (
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Courier:</span>
                    {order.courierId ? (
                      courier ? (
                        <CourierChip courier={courier} />
                      ) : (
                        <span className="font-mono">{order.courierId}</span>
                      )
                    ) : (
                      <span className="text-muted-foreground">Not specified by buyer</span>
                    )}
                  </div>
                )}
                {order.trackingNumber && (
                  <p className="mt-3 text-xs">
                    <span className="text-muted-foreground">Tracking #: </span>
                    <span className="font-mono">{order.trackingNumber}</span>
                  </p>
                )}
                {order.trackingUrl && (
                  <p className="mt-1 text-xs">
                    <a
                      href={order.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline break-all"
                    >
                      {order.trackingUrl}
                    </a>
                  </p>
                )}
              </section>

              {order.status === 'to_pay' && (
                <section className="rounded-md border bg-muted/30 p-3 text-sm">
                  <p className="font-medium mb-1">Manual payment review</p>
                  <p className="text-xs text-muted-foreground">
                    Use the <span className="font-medium">Approve payment</span> button below to
                    view the proof and approve or reject the submitted payment.
                  </p>
                </section>
              )}

              <section>
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  Items
                </h3>
                <ul className="space-y-2">
                  {order.items.map((it) => {
                    const el = eligibilityQ.data?.items.find((i) => i.orderItemId === it.id);
                    return (
                      <li
                        key={it.id}
                        className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                      >
                        <div className="min-w-0">
                          <p className="font-medium truncate">
                            {it.productName ?? `${it.productId.slice(0, 8)}…`}
                            {it.variantName ? ` — ${it.variantName}` : ''}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            qty {it.qty} · {it.unitPrice}
                          </p>
                        </div>
                        {el && !el.refundable && (
                          <Badge variant="destructive" className="text-[10px]">
                            Code redeemed · non-refundable
                          </Badge>
                        )}
                        {el && el.refundable && el.transferredUnredeemedCount > 0 && (
                          <Badge variant="warning" className="text-[10px]">
                            {el.transferredUnredeemedCount} code
                            {el.transferredUnredeemedCount === 1 ? '' : 's'} transferred away
                          </Badge>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {eligibilityQ.data && !eligibilityQ.data.allRefundable && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {eligibilityQ.data.items.filter((i) => !i.refundable).length} of{' '}
                    {eligibilityQ.data.items.length} items have redeemed codes and cannot be
                    refunded.
                  </p>
                )}
              </section>

              {/* Stock lots / recipes: which lots and materials the order used. */}
              <OrderStockUsageSection orderId={order.id} />

              {/* Money breakdown. The drawer showed no money at all, which made
                  setting a shipping fee blind arithmetic. `getOrder` already
                  returns every field. */}
              <section className="pt-2 border-t">
                <h4 className="mb-1 text-xs font-semibold text-muted-foreground">Payment</h4>
                <dl className="space-y-0.5 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Method</dt>
                    <dd>{order.paymentMethodLabel ?? '—'}</dd>
                  </div>
                  <MoneyRow label="Subtotal" value={order.subtotal} />
                  {Number(order.discountTotal) > 0 && (
                    <MoneyRow label="Discount" value={`−${order.discountTotal}`} />
                  )}
                  <MoneyRow
                    label="Shipping"
                    value={actionsQ.data?.awaitingShippingFee ? 'Awaiting fee' : order.shippingFee}
                    muted={actionsQ.data?.awaitingShippingFee}
                  />
                  {/* One row per fee. The hardcoded 'Other fee' fallback moved
                      into `orderCustomFeeLines`, which is now the ONE reader
                      across the admin drawer, the cashier tape and the reprint —
                      so a pre-breakdown order can't render here and nowhere else. */}
                  {orderCustomFeeLines(order).map((f, i) => (
                    <MoneyRow key={i} label={f.label} value={f.amount} />
                  ))}
                  <div className="flex items-center justify-between border-t pt-1 font-semibold">
                    <dt>Total</dt>
                    <dd className="tabular-nums">{order.total}</dd>
                  </div>
                </dl>
              </section>

              <section className="flex items-center gap-2 pt-2 border-t">
                {actionsQ.data?.awaitingShippingFee && (
                  <Button
                    size="sm"
                    onClick={() =>
                      onActionRequested({
                        kind: 'shippingFee',
                        orderId: order.id,
                        orderCode: order.code,
                        subtotal: order.subtotal,
                        currentFee: order.shippingFee,
                      })
                    }
                  >
                    Set shipping fee
                  </Button>
                )}
                {(actionsQ.data?.admin ?? [])
                  .filter((e) => e.to !== 'cancelled' && e.to !== 'refunded')
                  .map((edge) => (
                    <Button
                      key={`${edge.from}-${edge.to}`}
                      size="sm"
                      onClick={() =>
                        onActionRequested({
                          kind: 'transition',
                          orderId: order.id,
                          orderCode: order.code,
                          from: edge.from,
                          to: edge.to,
                          needsTracking: edge.requires === 'tracking_number',
                        })
                      }
                    >
                      {PRIMARY_LABELS[edge.to] ?? edge.to}
                    </Button>
                  ))}
              </section>
            </TabsContent>

            <TabsContent value="packing">
              <PackingSlip order={order} />
            </TabsContent>

            {accountingEnabled && (
              <TabsContent value="billing">
                <BillingTab orderId={order.id} docs={documentsQ.data} />
              </TabsContent>
            )}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Printable packing slip. The `.packing-slip` element is the only thing that
 * survives `@media print` (see index.css). Designed for A4/Letter sheet — buyer
 * + ship-to address at the top in large type, then item rows with name + SKU +
 * a prominent qty column for the packer.
 */
function PackingSlip({ order }: { order: OrderDetail }) {
  const address = order.shipToAddress as ShipToAddress | null;
  const addressLines = formatAddressLines(address);
  const buyerName = order.buyer?.fullName || address?.fullName || order.buyer?.email || '';
  const phone = address?.phone || order.buyer?.phone || '';

  return (
    <div>
      <div className="no-print flex justify-end mb-3">
        <Button size="sm" variant="outline" onClick={() => window.print()}>
          Print
        </Button>
      </div>

      <div className="packing-slip border rounded-md p-6 bg-background text-foreground">
        <header className="flex items-start justify-between gap-6 pb-4 border-b">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">PACKING SLIP</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {new Date(order.createdAt).toLocaleString()}
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono text-sm">{order.code}</p>
            <p className="text-xs text-muted-foreground mt-0.5 uppercase">
              {ORDER_TYPE_LABELS[order.type] ?? order.type}
            </p>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 py-5 border-b">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Ship to
            </p>
            {buyerName && <p className="text-base font-semibold leading-tight">{buyerName}</p>}
            {addressLines.length ? (
              addressLines.map((line, i) => (
                <p key={i} className="text-sm leading-snug">
                  {line}
                </p>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No shipping address on file.</p>
            )}
            {phone && <p className="text-sm mt-1">☎ {phone}</p>}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Ship from
            </p>
            {order.shipFromBranch ? (
              <>
                <p className="text-base font-semibold leading-tight">{order.shipFromBranch.name}</p>
                <p className="text-xs font-mono text-muted-foreground">
                  {order.shipFromBranch.code}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">—</p>
            )}
            {order.trackingNumber && (
              <p className="text-xs mt-2">
                <span className="text-muted-foreground">Tracking: </span>
                <span className="font-mono">{order.trackingNumber}</span>
              </p>
            )}
          </div>
        </section>

        <section className="py-4">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
            Items to pack
          </p>
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-wide text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-2">SKU</th>
                <th className="py-2 pr-2">Item</th>
                <th className="py-2 pr-2 text-right">Qty</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it) => (
                <tr key={it.id} className="border-b last:border-0 align-top">
                  <td className="py-2 pr-2 font-mono text-xs whitespace-nowrap">
                    {it.productSku ?? `${it.productId.slice(0, 8)}…`}
                  </td>
                  <td className="py-2 pr-2">
                    <div>{it.productName ?? '—'}</div>
                    {it.variantName && (
                      <div className="mt-0.5 font-semibold">Variation: {it.variantName}</div>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-right font-semibold text-base tabular-nums">
                    × {it.qty}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <footer className="pt-3 border-t text-xs text-muted-foreground flex items-center justify-between">
          <span>Total items: {order.items.reduce((s, it) => s + it.qty, 0)}</span>
          <span className="font-mono">{order.code}</span>
        </footer>
      </div>
    </div>
  );
}

/**
 * Accounting Phase 2 — the Billing tab: issue + view the order's numbered
 * documents ("Billing Statement / Order Invoice" BILL-…, "Credit Memo" CM-…).
 * Only ONE document renders at a time — `.billing-doc` prints position:absolute
 * at the sheet origin, so two mounted at once would overlap on paper.
 */
function BillingTab({ orderId, docs }: { orderId: string; docs?: OrderDocumentsResponse }) {
  const queryClient = useQueryClient();
  const [view, setView] = useState<'billing_statement' | 'credit_memo' | null>(null);
  const issue = useMutation({
    mutationFn: (docType: 'billing_statement' | 'credit_memo') =>
      issueOrderDocument(orderId, docType),
    onSuccess: (_res, docType) => {
      queryClient.invalidateQueries({ queryKey: ['admin-order-documents', orderId] });
      setView(docType);
    },
  });

  if (!docs) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const billing = docs.documents.find((d) => d.docType === 'billing_statement');
  const memo = docs.documents.find((d) => d.docType === 'credit_memo');
  const active = view ?? (billing ? 'billing_statement' : memo ? 'credit_memo' : null);
  const activeDoc =
    active === 'credit_memo' ? memo : active === 'billing_statement' ? billing : undefined;

  return (
    <div>
      <div className="no-print mb-3 flex flex-wrap items-center gap-2">
        {billing && (
          <Button
            size="sm"
            variant={active === 'billing_statement' ? 'default' : 'outline'}
            onClick={() => setView('billing_statement')}
          >
            {billing.docNo}
          </Button>
        )}
        {memo && (
          <Button
            size="sm"
            variant={active === 'credit_memo' ? 'default' : 'outline'}
            onClick={() => setView('credit_memo')}
          >
            {memo.docNo}
          </Button>
        )}
        {!billing && docs.eligibility.billing && (
          <Button
            size="sm"
            disabled={issue.isPending}
            onClick={() => issue.mutate('billing_statement')}
          >
            Issue billing statement
          </Button>
        )}
        {!memo && docs.eligibility.creditMemo && (
          <Button
            size="sm"
            variant="outline"
            disabled={issue.isPending}
            onClick={() => issue.mutate('credit_memo')}
          >
            Issue credit memo
          </Button>
        )}
        {activeDoc && (
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => window.print()}>
            Print
          </Button>
        )}
      </div>
      {issue.isError && (
        <p className="no-print mb-3 text-sm text-destructive">{apiErrorMessage(issue.error)}</p>
      )}
      {activeDoc ? (
        <OrderDocumentView payload={activeDoc.payload} />
      ) : (
        <p className="text-sm text-muted-foreground">
          {docs.eligibility.billing || docs.eligibility.creditMemo
            ? 'No document issued yet.'
            : 'No document can be issued for this order — a billing statement needs a paid, cash-denominated house order; a credit memo needs a refunded one (or a billed order that was cancelled).'}
        </p>
      )}
    </div>
  );
}

/**
 * Renders an issued order document EXCLUSIVELY from its frozen payload — never
 * from live order/product data, so the print always matches what was issued.
 * Markup adapted from the /accounting/billing preview page; `.billing-doc` is
 * already whitelisted by the print stylesheet (index.css).
 */
function OrderDocumentView({ payload }: { payload: OrderDocumentPayload }) {
  const t = payload.totals;
  const nonZero = (v: string) => new Decimal(v || '0').abs().gt(0);
  return (
    <div className="billing-doc rounded-md border bg-background p-6 text-foreground">
      <header className="flex items-start justify-between gap-6 border-b pb-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight uppercase">{payload.title}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {new Date(payload.issuedAt).toLocaleString()}
            {payload.issuedByName ? ` · Issued by ${payload.issuedByName}` : ''}
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-sm">{payload.docNo}</p>
          <p className="mt-0.5 text-xs uppercase text-muted-foreground">
            Order {payload.orderCode}
          </p>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-6 border-b py-5">
        <div>
          <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            Billed to
          </p>
          <p className="text-base font-semibold leading-tight">{payload.buyer.name || '—'}</p>
          {payload.buyer.addressLines.map((line) => (
            <p key={line} className="text-sm leading-snug">
              {line}
            </p>
          ))}
          {payload.buyer.phone && <p className="mt-1 text-sm">{payload.buyer.phone}</p>}
          {payload.buyer.email && (
            <p className="text-xs text-muted-foreground">{payload.buyer.email}</p>
          )}
        </div>
        <div>
          <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            Issued by
          </p>
          <p className="text-base font-semibold leading-tight">
            {payload.company.name || payload.company.branchName || '—'}
          </p>
          {payload.company.branchName && payload.company.name && (
            <p className="text-sm leading-snug">{payload.company.branchName}</p>
          )}
          {payload.company.branchCode && (
            <p className="font-mono text-xs text-muted-foreground">{payload.company.branchCode}</p>
          )}
          {payload.company.address && (
            <p className="mt-1 text-sm leading-snug">{payload.company.address}</p>
          )}
          {payload.company.tin && (
            <p className="text-xs text-muted-foreground">TIN {payload.company.tin}</p>
          )}
          {payload.company.phone && (
            <p className="text-xs text-muted-foreground">{payload.company.phone}</p>
          )}
        </div>
      </section>

      {payload.refund && (
        <section className="border-b py-3 text-sm">
          <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Refund</p>
          <p>
            Refunded {new Date(payload.refund.refundedAt).toLocaleString()}
            {payload.refund.refundedByName ? ` by ${payload.refund.refundedByName}` : ''}
          </p>
          {payload.refund.note && (
            <p className="text-xs text-muted-foreground">{payload.refund.note}</p>
          )}
        </section>
      )}

      <section className="py-4">
        <p className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">Items</p>
        <table className="w-full text-sm">
          <thead className="border-b text-left text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="py-2 pr-2">SKU</th>
              <th className="py-2 pr-2">Item</th>
              <th className="py-2 pr-2 text-right">Qty</th>
              <th className="py-2 pr-2 text-right">Unit price</th>
              <th className="py-2 pr-2 text-right">VATable</th>
              <th className="py-2 pr-2 text-right">VAT</th>
              <th className="py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {payload.lines.map((l, i) => (
              <tr key={i} className="border-b align-top last:border-0">
                <td className="whitespace-nowrap py-2 pr-2 font-mono text-xs">{l.sku ?? '—'}</td>
                <td className="py-2 pr-2">
                  {l.name}
                  {l.variantName && (
                    <span className="text-xs text-muted-foreground"> · {l.variantName}</span>
                  )}
                  {l.taxCode && l.taxCode !== 'vatable' && (
                    <span className="ml-1 text-[10px] uppercase text-muted-foreground">
                      {l.taxCode === 'exempt' ? 'VAT-exempt' : 'Zero-rated'}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-2 text-right tabular-nums">{l.qty}</td>
                <td className="py-2 pr-2 text-right font-mono tabular-nums">{l.unitPrice}</td>
                <td className="py-2 pr-2 text-right font-mono tabular-nums text-muted-foreground">
                  {l.vatable}
                </td>
                <td className="py-2 pr-2 text-right font-mono tabular-nums text-muted-foreground">
                  {l.vat}
                </td>
                <td className="py-2 text-right font-mono text-base font-semibold tabular-nums">
                  {l.lineTotal}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="flex justify-end border-t pt-4">
        <div className="w-full max-w-xs space-y-1 text-sm">
          <TotalsRow label="VATable sales" value={t.vatable} />
          <TotalsRow label="VAT" value={t.vat} />
          {nonZero(t.exempt) && <TotalsRow label="VAT-exempt sales" value={t.exempt} />}
          {nonZero(t.zeroRated) && <TotalsRow label="Zero-rated sales" value={t.zeroRated} />}
          {nonZero(t.untagged) && <TotalsRow label="Untagged sales" value={t.untagged} />}
          <div className="flex justify-between border-t pt-1">
            <span className="text-muted-foreground">Goods total (VAT inclusive)</span>
            <span className="font-mono tabular-nums">{t.goods}</span>
          </div>
          {nonZero(t.coupon) && <TotalsRow label="Coupon discount" value={`−${t.coupon}`} />}
          {nonZero(t.managerDiscount) && (
            <TotalsRow label="Manager discount" value={`−${t.managerDiscount}`} />
          )}
          {nonZero(t.shipping) && <TotalsRow label="Shipping" value={t.shipping} />}
          {nonZero(t.customFees) && <TotalsRow label="Fees" value={t.customFees} />}
          {/* total = …components − rounding, so the printed row shows the negation. */}
          {nonZero(t.rounding) && (
            <TotalsRow label="Rounding" value={new Decimal(t.rounding).negated().toFixed(2)} />
          )}
          <div className="flex justify-between border-t pt-2 text-base font-semibold">
            <span>
              {payload.docType === 'credit_memo' ? 'Total amount credited' : 'Total amount due'}
            </span>
            <span className="font-mono tabular-nums">{t.total}</span>
          </div>
        </div>
      </section>

      <footer className="flex items-center justify-between gap-4 border-t pt-3 text-xs text-muted-foreground">
        <span>{payload.footer}</span>
        <span className="font-mono">{payload.docNo}</span>
      </footer>
    </div>
  );
}

function TotalsRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}

function ActionConfirmDialog({
  pending,
  onClose,
  onSuccess,
  onError,
}: {
  // `shippingFee` has its own dialog (SetShippingFeeDialog) — this one is the
  // state-machine transition / cancel / refund confirmer.
  pending: Exclude<PendingAction, { kind: 'shippingFee' }> | null;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Eligibility for the refund AND cancel confirmations: both statuses void
  // this order's live codes, so both need the transferred-code warning.
  const eligibilityQ = useQuery({
    queryKey: ['admin-order-eligibility', pending?.orderId],
    queryFn: async () =>
      (await api.get<RefundEligibility>(`/admin/orders/${pending!.orderId}/refund-eligibility`))
        .data,
    enabled: pending?.kind === 'refund' || pending?.kind === 'cancel',
  });

  // Order detail for the ordered-item list shown in the dialog.
  const detailQ = useQuery({
    queryKey: ['admin-order-detail', pending?.orderId],
    queryFn: async () => (await api.get<OrderDetail>(`/orders/${pending!.orderId}`)).data,
    enabled: !!pending,
  });

  // Resolve the buyer's chosen courier (shown in the Ship dialog so staff know
  // which courier to hand the parcel to). Shared cache key with the Orders page.
  const couriersQ = useQuery({ queryKey: ['courier-labels'], queryFn: fetchCourierLabels });
  const courier = couriersQ.data?.find((c) => c.id === detailQ.data?.courierId) ?? null;

  const mutation = useMutation({
    meta: { successMessage: 'Order updated.' },
    mutationFn: async () => {
      if (!pending) return;
      if (pending.kind === 'transition' && pending.needsTracking) {
        return api.post(`/admin/orders/${pending.orderId}/ship`, { trackingNumber });
      }
      if (pending.kind === 'transition' && pending.to === 'delivered') {
        return api.post(`/admin/orders/${pending.orderId}/deliver`, {});
      }
      const status =
        pending.kind === 'cancel'
          ? 'cancelled'
          : pending.kind === 'refund'
            ? 'refunded'
            : pending.to;
      return api.post(`/admin/orders/${pending.orderId}/status`, { status });
    },
    onSuccess: () => {
      setTrackingNumber('');
      setErrorMsg(null);
      onSuccess();
    },
    onError: (err: unknown) => {
      const e = err as {
        response?: { data?: { error?: string; message?: string; existingOrderCode?: string } };
      };
      const data = e.response?.data;
      const msg =
        data?.error === 'tracking_number_in_use'
          ? data.existingOrderCode
            ? `Tracking # already used on order ${data.existingOrderCode}.`
            : 'That tracking # is already used on another order.'
          : (data?.message ?? data?.error ?? 'Failed');
      setErrorMsg(msg);
      onError(msg);
    },
  });

  if (!pending) return null;

  const title = (() => {
    if (pending.kind === 'cancel') return `Cancel order ${pending.orderCode}?`;
    if (pending.kind === 'refund') return `Refund order ${pending.orderCode}?`;
    if (pending.needsTracking) return `Ship order ${pending.orderCode}`;
    return `${PRIMARY_LABELS[pending.to] ?? pending.to} · ${pending.orderCode}`;
  })();

  // Codes the buyer already GAVE AWAY die with the order, in a downline's vault,
  // with no un-void. The admin has no other way to know, so say it before they
  // confirm. Shown on cancel too — `cancelled` voids codes exactly like
  // `refunded` does.
  const transferredCount = eligibilityQ.data?.transferredUnredeemedTotal ?? 0;
  const transferredWarning =
    transferredCount > 0 ? (
      <>
        {' '}
        <strong>
          {transferredCount} unredeemed code{transferredCount === 1 ? '' : 's'} from this order{' '}
          {transferredCount === 1 ? 'has' : 'have'} been transferred to another member and will be
          voided in their vault.
        </strong>{' '}
        This cannot be undone.
      </>
    ) : null;

  const description = (() => {
    if (pending.kind === 'cancel') {
      return (
        <>
          This will mark the order as cancelled and restock any deducted inventory.
          {transferredWarning}
        </>
      );
    }
    if (pending.kind === 'refund') {
      if (!eligibilityQ.data) return <>Checking eligibility…</>;
      if (!eligibilityQ.data.anyRefundable) {
        return <>All items in this order have redeemed kit codes and cannot be refunded.</>;
      }
      const blocked = eligibilityQ.data.items.filter((i) => !i.refundable);
      if (blocked.length === 0) {
        return (
          <>
            This will mark the order as refunded and restock inventory.
            {transferredWarning}
          </>
        );
      }
      return (
        <>
          {eligibilityQ.data.items.length - blocked.length} of {eligibilityQ.data.items.length}{' '}
          items are refundable. Items with redeemed codes stay non-refundable:{' '}
          <em>{blocked.map((b) => b.productName).join(', ')}</em>.{transferredWarning}
        </>
      );
    }
    if (pending.needsTracking) {
      return <>Enter the courier tracking number and confirm to advance the order to shipping.</>;
    }
    return (
      <>
        Move order from{' '}
        <span className="font-mono">{STATUS_LABELS[pending.from] ?? pending.from}</span> to{' '}
        <span className="font-mono">{STATUS_LABELS[pending.to] ?? pending.to}</span>?
      </>
    );
  })();

  const confirmLabel =
    pending.kind === 'cancel'
      ? 'Cancel order'
      : pending.kind === 'refund'
        ? 'Refund'
        : pending.needsTracking
          ? 'Ship'
          : 'Confirm';
  const destructive = pending.kind === 'cancel' || pending.kind === 'refund';
  const disabledForTracking =
    pending.kind === 'transition' && pending.needsTracking && !trackingNumber.trim();
  const disabledForRefund =
    pending.kind === 'refund' && eligibilityQ.data && !eligibilityQ.data.anyRefundable;

  return (
    <ConfirmDialog
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          setTrackingNumber('');
          setErrorMsg(null);
          onClose();
        }
      }}
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      confirmVariant={destructive ? 'destructive' : 'default'}
      onConfirm={async () => {
        await mutation.mutateAsync();
      }}
      errorMessage={errorMsg}
      loading={mutation.isPending || (disabledForTracking ?? false) || (disabledForRefund ?? false)}
    >
      <div className="space-y-3">
        {/* Ordered items, so the admin sees what's in the order before confirming. */}
        {detailQ.data && detailQ.data.items.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Ordered items ({detailQ.data.items.reduce((s, it) => s + it.qty, 0)})
            </div>
            <div className="max-h-44 divide-y overflow-y-auto rounded-md border">
              {detailQ.data.items.map((it) => (
                <div
                  key={it.id}
                  className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
                >
                  <span className="min-w-0 truncate">
                    {it.productName ?? it.productSku ?? '—'}
                    {it.variantName ? ` — ${it.variantName}` : ''}
                  </span>
                  <span className="shrink-0 font-mono text-xs text-muted-foreground tabular-nums">
                    ×{it.qty} · {it.unitPrice}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {pending.kind === 'transition' && pending.needsTracking && (
          <div className="space-y-2">
            {/* Always show a courier line so it's never silently missing: resolve
                the buyer's chosen courier to a chip, fall back to the raw id if it
                no longer maps to an enabled courier, else say none was selected. */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Courier:</span>
              {detailQ.data?.courierId ? (
                courier ? (
                  <CourierChip courier={courier} />
                ) : (
                  <span className="font-mono">{detailQ.data.courierId}</span>
                )
              ) : (
                <span className="text-muted-foreground">Not specified by buyer</span>
              )}
            </div>
            <label className="text-xs text-muted-foreground">Tracking number</label>
            <Input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="e.g. JT0123456789"
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground">
              The clickable tracking link is built from the courier's tracking-URL template
              (Shipping settings) — you only enter the number.
            </p>
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
}

/**
 * Approve / reject a manual payment from the Orders page. Renders the
 * submitted proof + reference # + amount inline (instead of bouncing the
 * admin to the Payments page) and routes through the canonical
 * `/payments/manual/:id/review` endpoint so the comp event fires with the
 * proper `manual_payment-${id}` event ID.
 */
function ApprovePaymentDialog({
  orderId,
  orderCode,
  onClose,
  onSuccess,
  onError,
}: {
  orderId: string;
  orderCode: string;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  // QoL Slice 3 — admin types the reference from the picture; the member's
  // value is revealed only after the verify call commits. `verifyResult`
  // carries that revealed value (so we can show match/mismatch) and acts as
  // the gate for Approve.
  const [adminRef, setAdminRef] = useState('');
  const [verifyResult, setVerifyResult] = useState<{
    match: boolean;
    adminTypedReference: string;
    memberTypedReference: string | null;
  } | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  // Recovery: record an offline / out-of-band payment when the order has NO
  // member-uploaded proof (the "manual pay never asked for a photo" bug, or a
  // genuinely offline payment). Marks the order paid via the same approval path
  // so kit codes + comp release exactly like an approved proof.
  const [offlineMethod, setOfflineMethod] = useState('Offline payment');
  const [offlineReference, setOfflineReference] = useState('');
  const [offlineNote, setOfflineNote] = useState('');
  // "Approve anyway" — approve an unverified proof when the reference is
  // unreadable. Offered only when the tenant enabled the override; the written
  // reason is mandatory and lands in the audit log.
  const [forceMode, setForceMode] = useState(false);
  const [forceReason, setForceReason] = useState('');
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const forceAllowed = featuresQ.data?.manual_payment_force_approve === true;

  const paymentQ = useQuery({
    queryKey: ['admin-order-manual-payment', orderId],
    queryFn: async () =>
      (await api.get<ManualPayment>(`/admin/orders/${orderId}/manual-payment`)).data,
    retry: false,
  });

  // Pre-populate the verified state when the dialog reopens for an already-
  // verified pending payment, so the admin doesn't have to re-type.
  if (paymentQ.data?.adminTypedReference && !verifyResult && paymentQ.data.status === 'pending') {
    // We intentionally don't know the member value at this point; the verify
    // route returns it. Leave it null — the modal renders "verified" without
    // forcing a mismatch label.
    setVerifyResult({
      match: true,
      adminTypedReference: paymentQ.data.adminTypedReference,
      memberTypedReference: null,
    });
  }

  const verifyMut = useMutation({
    meta: { successMessage: 'Reference verified.' },
    mutationFn: async () => {
      if (!paymentQ.data) throw new Error('no_manual_payment');
      const res = await api.post<{
        match: boolean;
        adminTypedReference: string;
        memberTypedReference: string | null;
      }>(`/payments/manual/${paymentQ.data.id}/verify-reference`, {
        adminTypedReference: adminRef.trim(),
      });
      return res.data;
    },
    onSuccess: (data) => {
      setVerifyResult(data);
      setVerifyError(null);
    },
    onError: (err: unknown) => {
      const data = (
        err as {
          response?: { data?: { error?: string; existingOrderCode?: string; message?: string } };
        }
      ).response?.data;
      if (data?.error === 'duplicate_admin_reference') {
        setVerifyError(
          data.existingOrderCode
            ? `That reference has already been approved on order ${data.existingOrderCode}.`
            : 'That reference has already been approved on another order.',
        );
      } else {
        setVerifyError(data?.message ?? 'Could not verify reference.');
      }
    },
  });

  const mut = useMutation({
    meta: { successMessage: 'Payment reviewed.' },
    mutationFn: async (decision: 'approved' | 'rejected') => {
      if (!paymentQ.data) throw new Error('no_manual_payment');
      const body: {
        decision: 'approved' | 'rejected';
        rejectionReason?: string;
        force?: boolean;
        forceReason?: string;
      } = { decision };
      if (decision === 'rejected') body.rejectionReason = rejectionReason || undefined;
      if (decision === 'approved' && forceMode && !verifyResult) {
        body.force = true;
        body.forceReason = forceReason.trim();
      }
      return api.post(`/payments/manual/${paymentQ.data.id}/review`, body);
    },
    onSuccess: () => {
      setRejectMode(false);
      setRejectionReason('');
      setErrMsg(null);
      setAdminRef('');
      setVerifyResult(null);
      setVerifyError(null);
      setForceMode(false);
      setForceReason('');
      onSuccess();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string; message?: string } } };
      const msg = e.response?.data?.message ?? e.response?.data?.error ?? 'Failed';
      setErrMsg(msg);
      onError(msg);
    },
  });

  const recordOfflineMut = useMutation({
    meta: { successMessage: 'Offline payment recorded.' },
    mutationFn: async () =>
      api.post(`/admin/orders/${orderId}/record-offline-payment`, {
        method: offlineMethod.trim() || undefined,
        reference: offlineReference.trim() || undefined,
        note: offlineNote.trim() || undefined,
      }),
    onSuccess: () => {
      setErrMsg(null);
      onSuccess();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string; message?: string } } };
      const msg = e.response?.data?.message ?? e.response?.data?.error ?? 'Failed';
      setErrMsg(msg);
      onError(msg);
    },
  });

  const noPayment = paymentQ.isError;
  const payment = paymentQ.data;
  const isImage =
    payment?.proofUrl && /\.(png|jpe?g|gif|webp|bmp)$/i.test(payment.proofR2Key ?? '');
  const verified = verifyResult !== null;
  const mismatch = verified && !verifyResult.match;

  return (
    <Dialog
      open={true}
      onOpenChange={(o) => {
        if (!o && !mut.isPending) {
          setRejectMode(false);
          setRejectionReason('');
          setErrMsg(null);
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Approve payment · {orderCode}</DialogTitle>
        </DialogHeader>

        {paymentQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading payment…</p>
        ) : noPayment ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              No payment proof was submitted for this order (the buyer may not have been shown an
              upload form, or paid out-of-band). Record an offline payment to mark the order paid —
              kit codes and commissions release exactly like an approved proof.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Method</label>
                <Input
                  value={offlineMethod}
                  onChange={(e) => setOfflineMethod(e.target.value)}
                  placeholder="e.g. GCash, Bank transfer"
                  disabled={recordOfflineMut.isPending}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Reference (optional)</label>
                <Input
                  value={offlineReference}
                  onChange={(e) => setOfflineReference(e.target.value)}
                  placeholder="e.g. 1234567890"
                  disabled={recordOfflineMut.isPending}
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                Note (optional, for the audit log)
              </label>
              <Input
                value={offlineNote}
                onChange={(e) => setOfflineNote(e.target.value)}
                placeholder="e.g. paid in cash at the office"
                disabled={recordOfflineMut.isPending}
              />
            </div>
          </div>
        ) : payment ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="font-mono">
                  {payment.amount} {payment.currency}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Method</p>
                <p>{payment.method}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Scan</p>
                <p>
                  <Badge variant={payment.scanStatus === 'clean' ? 'success' : 'secondary'}>
                    {payment.scanStatus ?? 'unknown'}
                  </Badge>
                </p>
              </div>
            </div>

            {payment.proofUrl ? (
              <div className="rounded-md border overflow-hidden bg-muted">
                {isImage ? (
                  <a href={payment.proofUrl} target="_blank" rel="noreferrer">
                    <img
                      src={payment.proofUrl}
                      alt="Payment proof"
                      className="w-full max-h-96 object-contain"
                    />
                  </a>
                ) : (
                  <a
                    href={payment.proofUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="block p-4 text-sm text-primary underline"
                  >
                    Open proof file →
                  </a>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No proof file attached.</p>
            )}

            {payment.status === 'pending' && (
              <div className="space-y-2 pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Type the reference number from the picture.{' '}
                  {payment.referenceProvided
                    ? "The member's value is revealed for comparison after you commit."
                    : 'The member did not submit a reference.'}
                </p>
                {!verified ? (
                  <div className="flex gap-2">
                    <Input
                      value={adminRef}
                      onChange={(e) => setAdminRef(e.target.value)}
                      placeholder="Reference from picture"
                      disabled={verifyMut.isPending}
                    />
                    <Button
                      onClick={() => verifyMut.mutate()}
                      disabled={!adminRef.trim() || verifyMut.isPending}
                    >
                      {verifyMut.isPending ? 'Verifying…' : 'Verify'}
                    </Button>
                  </div>
                ) : (
                  <div
                    className={
                      'rounded-md border p-2 text-xs ' +
                      (mismatch
                        ? 'border-warning/40 bg-warning/10 text-foreground'
                        : 'border-success/40 bg-success/10 text-foreground')
                    }
                  >
                    {mismatch ? (
                      <>
                        <p className="font-medium">⚠ Mismatch</p>
                        <p>
                          You:{' '}
                          <span className="font-mono">{verifyResult!.adminTypedReference}</span> ·
                          Member:{' '}
                          <span className="font-mono">
                            {verifyResult!.memberTypedReference ?? '— (none)'}
                          </span>
                        </p>
                      </>
                    ) : (
                      <p>
                        ✓ Verified:{' '}
                        <span className="font-mono">{verifyResult!.adminTypedReference}</span>
                      </p>
                    )}
                    {/* Item 7: let the admin correct a mistyped reference before
                        approving — re-verifying overwrites the stored value. */}
                    <button
                      type="button"
                      className="mt-1 text-xs underline opacity-80 hover:opacity-100"
                      onClick={() => {
                        setAdminRef(verifyResult!.adminTypedReference);
                        setVerifyResult(null);
                        setVerifyError(null);
                      }}
                    >
                      Change reference
                    </button>
                  </div>
                )}
                {verifyError && <p className="text-sm text-destructive">{verifyError}</p>}
                {forceAllowed && !verified && !forceMode && (
                  <button
                    type="button"
                    className="text-xs underline text-muted-foreground hover:text-foreground"
                    onClick={() => setForceMode(true)}
                  >
                    Can't read the reference? Approve anyway…
                  </button>
                )}
                {forceAllowed && !verified && forceMode && (
                  <div className="space-y-1 rounded-md border border-warning/40 bg-warning/10 p-2">
                    <p className="text-xs font-medium">⚠ Approving without verification</p>
                    <p className="text-xs text-muted-foreground">
                      Recorded in the audit log against your account.
                    </p>
                    <Input
                      value={forceReason}
                      onChange={(e) => setForceReason(e.target.value)}
                      placeholder="Why? e.g. reference cropped out of the screenshot"
                    />
                    <button
                      type="button"
                      className="text-xs underline opacity-80 hover:opacity-100"
                      onClick={() => {
                        setForceMode(false);
                        setForceReason('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}

            {payment.status !== 'pending' && (
              <p className="text-xs text-warning">
                This payment is already {payment.status}.
                {payment.rejectionReason && ` Reason: ${payment.rejectionReason}`}
              </p>
            )}
          </div>
        ) : null}

        {rejectMode && (
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Rejection reason (optional)</label>
            <Input
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g. proof unreadable, reference doesn't match"
              autoFocus
            />
          </div>
        )}

        {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}

        <DialogFooter>
          {!rejectMode ? (
            <>
              <Button variant="outline" onClick={() => onClose()} disabled={mut.isPending}>
                Close
              </Button>
              {payment && payment.status === 'pending' && (
                <Button
                  variant="destructive"
                  onClick={() => setRejectMode(true)}
                  disabled={mut.isPending}
                >
                  Reject…
                </Button>
              )}
              <Button
                onClick={() => (noPayment ? recordOfflineMut.mutate() : mut.mutate('approved'))}
                disabled={
                  mut.isPending ||
                  recordOfflineMut.isPending ||
                  (noPayment
                    ? !offlineMethod.trim()
                    : payment
                      ? payment.status !== 'pending' ||
                        // Unlocked by a verified reference, OR an explicit
                        // forced approval carrying a written reason.
                        (!verified && !(forceMode && forceReason.trim()))
                      : false)
                }
              >
                {noPayment
                  ? recordOfflineMut.isPending
                    ? 'Recording…'
                    : 'Record offline payment'
                  : mut.isPending && mut.variables === 'approved'
                    ? 'Approving…'
                    : !verified && forceMode
                      ? 'Approve without verifying'
                      : mismatch
                        ? 'Approve anyway'
                        : 'Approve & mark paid'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setRejectMode(false)}
                disabled={mut.isPending}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={() => mut.mutate('rejected')}
                disabled={mut.isPending}
              >
                {mut.isPending && mut.variables === 'rejected' ? 'Rejecting…' : 'Confirm reject'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Price a manual-fee order. Shows the live new total so the admin isn't doing
 * the arithmetic in their head, and states plainly that this is what unblocks
 * the buyer's payment.
 */
function SetShippingFeeDialog({
  pending,
  onClose,
  onSuccess,
  onError,
}: {
  pending: Extract<PendingAction, { kind: 'shippingFee' }>;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [fee, setFee] = useState(Number(pending.currentFee) > 0 ? pending.currentFee : '');
  const [note, setNote] = useState('');

  const parsed = fee.trim() === '' ? null : new Decimal(fee.trim() || '0');
  const newTotal = parsed && !parsed.isNaN() ? new Decimal(pending.subtotal).plus(parsed) : null;

  const mutation = useMutation({
    meta: { successMessage: 'Shipping fee set — the buyer can now pay.' },
    mutationFn: () =>
      api.post(`/admin/orders/${pending.orderId}/shipping-fee`, {
        shippingFee: parsed!.toFixed(4),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess,
    onError: (err) => onError(apiErrorMessage(err, 'Could not set the shipping fee.')),
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Set shipping fee — {pending.orderCode}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            This order was placed with a &ldquo;manually set shipping&rdquo; courier, so the buyer
            has not been charged for delivery yet. Setting the fee moves the order to{' '}
            <strong>To Pay</strong> and notifies them.
          </p>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">Shipping fee</p>
            <MoneyInput value={fee} onChange={(e) => setFee(e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">Note (optional)</p>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. quoted by phone"
            />
          </div>
          <dl className="space-y-0.5 rounded-md border p-2">
            <MoneyRow label="Goods" value={pending.subtotal} />
            <MoneyRow label="Shipping" value={parsed ? parsed.toFixed(2) : '—'} />
            <div className="flex items-center justify-between border-t pt-1 font-semibold">
              <dt>New total</dt>
              <dd className="tabular-nums">{newTotal ? newTotal.toFixed(2) : '—'}</dd>
            </div>
          </dl>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!parsed || parsed.isNaN() || parsed.isNegative() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving…' : 'Set fee'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
